<script>
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';

// O e-mail nao entra aqui: ele deixou de ser editado inline e passou a ter
// dialogo proprio (ChangeEmailModal), porque trocar o e-mail troca a
// credencial de login do Aceleres.
const FIELD_VALIDATION = {
  name: 'userName',
  displayName: 'userDisplayName',
};

export default {
  props: {
    name: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    displayName: {
      type: String,
      default: '',
    },
    emailEnabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['updateUser', 'requestEmailChange'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      userName: this.name,
      userDisplayName: this.displayName,
      userEmail: this.email,
      editingField: null,
    };
  },
  validations: {
    userName: {
      required,
      minLength: minLength(1),
    },
    userDisplayName: {},
  },
  watch: {
    name: {
      handler(value) {
        this.userName = value;
      },
      immediate: true,
    },
    displayName: {
      handler(value) {
        this.userDisplayName = value;
      },
      immediate: true,
    },
    email: {
      handler(value) {
        this.userEmail = value;
      },
      immediate: true,
    },
  },
  methods: {
    // Cada campo tem seu proprio botao de salvar, entao "editar e desistir"
    // precisa voltar ao ultimo valor gravado (que chega pelos props). Sem
    // isso o input continuava mostrando o texto abandonado como se estivesse
    // salvo, e o save de OUTRO campo levava esse texto junto no payload.
    discardField(field) {
      if (field === 'name') this.userName = this.name;
      if (field === 'displayName') this.userDisplayName = this.displayName;
      this.v$[FIELD_VALIDATION[field]]?.$reset();
    },
    cancelField(field) {
      this.discardField(field);
      this.editingField = null;
    },
    activateField(field) {
      if (this.editingField && this.editingField !== field) {
        this.discardField(this.editingField);
      }
      this.editingField = field;
      const inputRef = {
        name: 'profileNameInput',
        displayName: 'profileDisplayNameInput',
      }[field];
      this.$nextTick(() => this.$refs[inputRef]?.focus());
    },
    saveField(field) {
      const validation = this.v$[FIELD_VALIDATION[field]];
      validation?.$touch();

      if (validation?.$invalid) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ERROR'));
        return;
      }

      // So o campo em edicao viaja com o valor local; os outros vao com o
      // ultimo valor gravado, para que uma edicao pendente em outro campo
      // nunca seja salva sem o usuario ter confirmado.
      this.$emit('updateUser', {
        name: field === 'name' ? this.userName : this.name,
        displayName:
          field === 'displayName' ? this.userDisplayName : this.displayName,
        email: this.email,
      });
      this.editingField = null;
    },
  },
};
</script>

<template>
  <section class="profile-basic-details flex flex-col gap-4">
    <div
      class="profile-basic-details__field"
      :class="{
        'profile-basic-details__field--editing': editingField === 'name',
        'profile-basic-details__field--error': v$.userName.$error,
      }"
    >
      <label class="profile-basic-details__label" for="profile-name">
        {{ $t('PROFILE_SETTINGS.FORM.NAME.LABEL') }}
      </label>
      <div class="profile-basic-details__input-row">
        <input
          id="profile-name"
          ref="profileNameInput"
          v-model="userName"
          class="profile-basic-details__input"
          :readonly="editingField !== 'name'"
          type="text"
          :placeholder="$t('PROFILE_SETTINGS.FORM.NAME.PLACEHOLDER')"
          @input="v$.userName.$touch"
          @keydown.enter.prevent="saveField('name')"
          @keydown.esc="cancelField('name')"
        />
        <button
          v-if="editingField === 'name'"
          type="button"
          class="profile-basic-details__action profile-basic-details__action--save"
          aria-label="Salvar nome"
          @click="saveField('name')"
        >
          <i class="i-lucide-check" aria-hidden="true" />
        </button>
        <button
          v-else
          type="button"
          class="profile-basic-details__action"
          aria-label="Editar nome"
          @click="activateField('name')"
        >
          <i class="i-lucide-pencil" aria-hidden="true" />
        </button>
      </div>
      <p v-if="v$.userName.$error" class="profile-basic-details__error">
        {{ $t('PROFILE_SETTINGS.FORM.NAME.ERROR') }}
      </p>
    </div>

    <div
      class="profile-basic-details__field"
      :class="{
        'profile-basic-details__field--editing': editingField === 'displayName',
      }"
    >
      <label class="profile-basic-details__label" for="profile-display-name">
        {{ $t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.LABEL') }}
      </label>
      <div class="profile-basic-details__input-row">
        <input
          id="profile-display-name"
          ref="profileDisplayNameInput"
          v-model="userDisplayName"
          class="profile-basic-details__input"
          :readonly="editingField !== 'displayName'"
          type="text"
          :placeholder="$t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.PLACEHOLDER')"
          @keydown.enter.prevent="saveField('displayName')"
          @keydown.esc="cancelField('displayName')"
        />
        <button
          v-if="editingField === 'displayName'"
          type="button"
          class="profile-basic-details__action profile-basic-details__action--save"
          aria-label="Salvar nome de exibição"
          @click="saveField('displayName')"
        >
          <i class="i-lucide-check" aria-hidden="true" />
        </button>
        <button
          v-else
          type="button"
          class="profile-basic-details__action"
          aria-label="Editar nome de exibição"
          @click="activateField('displayName')"
        >
          <i class="i-lucide-pencil" aria-hidden="true" />
        </button>
      </div>
    </div>

    <div v-if="emailEnabled" class="profile-basic-details__field">
      <label class="profile-basic-details__label" for="profile-email">
        {{ $t('PROFILE_SETTINGS.FORM.EMAIL.LABEL') }}
      </label>
      <div class="profile-basic-details__input-row">
        <input
          id="profile-email"
          class="profile-basic-details__input"
          :value="email"
          readonly
          type="email"
          :placeholder="$t('PROFILE_SETTINGS.FORM.EMAIL.PLACEHOLDER')"
        />
        <button
          type="button"
          class="profile-basic-details__action"
          :aria-label="$t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.TITLE')"
          @click="$emit('requestEmailChange')"
        >
          <i class="i-lucide-pencil" aria-hidden="true" />
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.profile-basic-details {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.profile-basic-details__field {
  min-width: 0;
}

.profile-basic-details__label {
  display: block;
  margin-bottom: 7px;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.045em;
  line-height: 1.3;
  text-transform: uppercase;
}

.profile-basic-details__input-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  box-shadow: inset 0 -2px 0 transparent;
  transition: box-shadow 160ms ease;
}

.profile-basic-details__field--editing .profile-basic-details__input-row {
  box-shadow: inset 0 -2px 0 #1b8755;
}

.profile-basic-details__field--error .profile-basic-details__input-row {
  box-shadow: inset 0 -2px 0 #dc2626;
}

.profile-basic-details__input {
  width: 100%;
  min-width: 0;
  height: 42px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #111827;
  font-size: 1rem;
  line-height: 42px;
  outline: none;
}

#profile-name {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.profile-basic-details__input::placeholder {
  color: #9ca3af;
}

.profile-basic-details__input[readonly] {
  cursor: default;
}

.profile-basic-details__action {
  display: inline-flex;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #6b7280;
  transition: background-color 160ms ease, color 160ms ease;
}

.profile-basic-details__action:hover,
.profile-basic-details__action:focus-visible {
  background: #f3f4f6;
  color: #1b8755;
  outline: none;
}

.profile-basic-details__action--save {
  color: #1b8755;
}

.profile-basic-details__action i {
  width: 19px;
  height: 19px;
  font-size: 19px;
}

.profile-basic-details__error {
  margin: 6px 0 0;
  color: #dc2626;
  font-size: 0.75rem;
  line-height: 1.35;
}

:global(.dark) .profile-basic-details__label {
  color: #a1a1aa;
}

:global(.dark) .profile-basic-details__input {
  color: #f3f4f6;
}

:global(.dark) .profile-basic-details__action:hover,
:global(.dark) .profile-basic-details__action:focus-visible {
  background: #2a2f35;
}
</style>
